/*
 * indonesian-morphology.js — Nazief–Adriani / Enhanced Confix-Stripping (ECS)
 * morphological analyzer for Bahasa Indonesia. Decomposes a word into
 * awalan (prefix) + kata dasar (root) + akhiran (suffix) [+ sisipan/infix],
 * resolving morphophonemic nasal assimilation (meN-/peN-) via dictionary-
 * backed recoding.
 *
 * Dictionary: public/data/indonesian/kata-dasar.txt — CC-BY-SA Wiktionary
 * lemma list (see NOTICES.txt). The dictionary is the algorithm's stop
 * condition; every strip is validated against it, so a garbage split can
 * never be emitted (a fully-unresolved word is returned as-is, found=false).
 *
 * Design (beyond textbook ECS, to reach schoolwork accuracy):
 *  1. ENUMERATE every valid analysis, then SCORE — avoids the "return-first"
 *     overstem (menemukan → te·-mu instead of temu·-kan).
 *  2. STRICT suffix morphotactics: clitics (‑lah/‑kah/‑pun · ‑ku/‑mu/‑nya)
 *     may only be stripped OUTSIDE derivational suffixes (root+deriv+poss+part),
 *     so an inner "‑mu" that is really part of the root is never taken.
 *  3. Lexicalized-form gate: when the whole word IS a dictionary entry, only
 *     prefer a decomposition that uses a COMPLEX prefix (meN-/peN-/ber-/ter-/per-),
 *     a suffix, or a clitic — never a bare plain prefix (di-/ke-/se-) alone.
 *     So berlari→ber+lari and apapun→apa+pun decompose, but kelas / sekolah /
 *     sepuluh stay whole.
 *  4. Ambiguity tie-break: longest root (mengarang→karang, not arang).
 *
 * Works in the browser (window.IndoMorph) and in Node (module.exports) so the
 * exact same core can be validated against the gold test set offline.
 *
 * Rule numbers = Confix-Stripping paper (Suhartono et al. 2014; original
 * Adriani/Nazief et al. ACM TALIP 2007). Deletion set {p,t,k,s}; deletion is
 * TRIED then rejected against the dictionary so loanwords that retain the
 * consonant (memproduksi→produksi) still resolve.
 */
(function (root, factory) {
    if (typeof module !== 'undefined' && module.exports) module.exports = factory();
    else root.IndoMorph = factory();
}(typeof globalThis !== 'undefined' ? globalThis : this, function () {
    'use strict';

    var VOWELS = 'aiueo';
    var isV = function (ch) { return ch && VOWELS.indexOf(ch) !== -1; };
    var syllables = function (w) { var n = 0; for (var i = 0; i < w.length; i++) if (isV(w.charAt(i))) n++; return n; };

    // Suffix classes and their morphotactic stage (outermost=0). Surface order is
    // ROOT + derivational + possessive + particle, so from the outside we strip
    // particle → possessive → derivational, and NEVER a lower stage after a higher.
    var PARTICLE   = ['lah', 'kah', 'tah', 'pun'];
    var POSSESSIVE = ['ku', 'mu', 'nya'];
    var DERIV      = ['kan', 'i', 'an']; // 'kan' before 'an' so the ‑k…‑an split is tried first
    var SUF_STAGE  = { particle: 0, possessive: 1, derivational: 2 };

    // Plain prefixes never mutate the root and are short → they collide with many
    // lexical words (kelas≠ke+las). Used by the lexicalized-form gate.
    var PLAIN_PREFIX = { 'di-': 1, 'ke-': 1, 'se-': 1 };

    // Closed-class function words (pronouns, question words, quantifiers,
    // conjunctions/prepositions, lexicalized se-/ter-/ber- adverbs) that must NEVER
    // decompose even though they look affixed (mereka≠me+reka, mengapa≠meng+apa,
    // seluruh≠se+luruh, berapa≠ber+apa). Checked before analysis; returned whole.
    var MONO = {};
    ('saya aku kamu kau engkau dikau anda dia ia beliau kami kita kalian mereka ' +
     'ini itu begini begitu sini situ sana ' +
     'apa siapa mengapa kenapa bagaimana gimana mana kemana dimana bilamana berapa kapan ' +
     'semua seluruh segala segenap seantero beberapa banyak sedikit setiap tiap masing para berbagai sebagian kebanyakan sejumlah beragam ' +
     'dan atau tetapi tapi karena sebab jika kalau bila ketika saat sewaktu selama sementara sedangkan sedang meski meskipun walau walaupun namun sehingga agar supaya untuk bagi oleh dengan tanpa kepada terhadap dari daripada pada dalam antara hingga sampai sejak setelah sesudah sebelum selain serta maupun yakni yaitu seperti bagai bagaikan laksana ' +
     'selalu senantiasa sering kadang jarang pernah sudah telah akan belum masih hanya cuma saja juga pula lagi malah justru bahkan tentu pasti mungkin barangkali agaknya rupanya kiranya sekitar kira hampir nyaris segera langsung terus kembali semakin makin kian sangat amat terlalu paling sekali benar betul sungguh sesungguhnya memang seharusnya sebaiknya sebenarnya sebetulnya ' +
     'sebuah seorang seekor sekarang sekalian sekaligus semula semacam sejenis sebagai sebagaimana secara sesuai sesuatu seseorang seolah seakan sebelah sekeliling seputar sepanjang selagi sembari sambil ' +
     'pertama seratus seribu sejuta ' +
     // lexicalized acronym: pemilu = pemilihan umum, not pe-·milu
     'pemilu').split(' ').forEach(function (w) { MONO[w] = 1; });

    // Forbidden prefix(canonical) × suffix pairs (Nazief–Adriani Table 5): if the
    // word already shed one of these suffixes, this prefix cannot be real → don't
    // strip it (kills overstemming). Exception: ke‑…‑i is allowed for root 'tahu'.
    var FORBIDDEN = {
        'ber-': ['i'],
        'di-':  ['an'],
        'ke-':  ['i', 'kan'],
        'meN-': ['an'],
        'se-':  ['i', 'kan'],
        'ter-': ['an']
    };

    // Curated infix (sisipan) roots the bare algorithm can't recover, plus an
    // algorithmic fallback for C+{el,em,er,in}+rest.
    // Every root here MUST exist in kata-dasar.txt, otherwise the entry silently does
    // nothing (the record is gated on dict.has). `gelembung` was listed with the root
    // `gembung`, which the Wiktionary-derived dictionary does not carry — dropped, so
    // the word is honestly returned as its own (lexicalized) root.
    var INFIX_CURATED = {
        telunjuk: ['tunjuk', '-el-'], gemuruh: ['guruh', '-em-'], gerigi: ['gigi', '-er-'],
        gemetar: ['getar', '-em-'], seruling: ['suling', '-er-'],
        kemuning: ['kuning', '-em-'], jemari: ['jari', '-em-'], sinambung: ['sambung', '-in-'],
        kinerja: ['kerja', '-in-'], temali: ['tali', '-em-']
    };
    var INFIXES = ['el', 'em', 'er', 'in'];

    // meN-/peN- + vowel is genuinely ambiguous when BOTH readings are dictionary
    // roots: mengarang = meng+karang (restore the deleted k) but mengubah = meng+ubah
    // (nothing was deleted). The dictionary carries 124 such k-pairs, 122 t-pairs and
    // 104 p-pairs, so the choice is not decidable from spelling alone.
    //
    // Default = DON'T restore: a restored root is only correct for a minority of the
    // ambiguous pairs, and always restoring shreds the most frequent verbs in the
    // language (mengubah→kubah, mengisi→kisi, mengakui→kaku, mengurus→kurus,
    // memasak→pasak). When the un-restored reading is NOT a dictionary word — which is
    // the common case (menyapu: "nyapu" ∉ dict) — the restored reading wins anyway
    // because it is the only analysis that terminates in a root, so this table is
    // needed only for the ambiguous minority.
    //
    // Listed here: roots where restoration IS correct despite an un-restored homograph.
    var RESTORE_OK = {};
    ('karang kurang kalah kata kaji kuasa kenal kembang ketik tabung tonton').split(' ').forEach(function (r) { RESTORE_OK[r] = 1; });

    // The mirror image: roots where the WEAK allomorph (me-·mV) is the right reading even
    // though a restored rival exists or the surface form is lexicalized — meminta→minta,
    // not mem-·pinta. Both tables are the honest limit of a dictionary-only analyzer: the
    // pair (pinta, minta) is structurally symmetric, and only lexical knowledge separates
    // them. Keep both lists short and test-covered rather than guessing at scale.
    var WEAK_OK = {};
    ('minta mula manipulasi').split(' ').forEach(function (r) { WEAK_OK[r] = 1; });

    // ── builders for the curated tables ──────────────────────────────────
    function px(canonical, surface, type) {
        return { canonical: canonical, surface: surface || canonical, type: type || 'prefix' };
    }
    function sx(surface, type) { return { surface: surface, type: type }; }
    function P(prefixes, rootWord, suffixes, isPhrase, infix) {
        return function (input) {
            return finalize(input, rootWord, prefixes, suffixes, infix || null, {
                found: true, isPhrase: !!isPhrase, curated: true
            });
        };
    }

    // Hand-curated exceptions for phrase-roots (the "kata dasar" is two words) and
    // an infix hidden inside a dict-listed surface. Endorsed by the spec — pure
    // ECS cannot reach these, and schoolwork tests them. prefixes are outer→in.
    var EXC = {
        pertanggungjawaban:      P([px('per-')], 'tanggung jawab', [sx('an', 'derivational')], true),
        mempertanggungjawabkan:  P([px('meN-', 'mem-'), px('per-')], 'tanggung jawab', [sx('kan', 'derivational')], true),
        ketidakadilan:           P([px('ke-'), px('tidak', 'tidak', 'negator')], 'adil', [sx('an', 'derivational')], false),
        ketidakpastian:          P([px('ke-'), px('tidak', 'tidak', 'negator')], 'pasti', [sx('an', 'derivational')], false),
        // sinambung ∈ dict short-circuits the ‑in‑ extraction, so force it here
        berkesinambungan:        P([px('ber-'), px('ke-')], 'sambung', [sx('an', 'derivational')], false, { surface: '-in-' }),
        // lexicalized clitic word (apapun ∈ dict → gate keeps it whole; force the split)
        apapun:                  P([], 'apa', [sx('pun', 'particle')], false),
        // lexicalized intermediate: ajaran ∈ dict, so fewest-affix stops at pem+bel+ajaran
        pembelajaran:            P([px('peN-', 'pem-'), px('ber-', 'bel-')], 'ajar', [sx('an', 'derivational')], false),
        pembelajar:              P([px('peN-', 'pem-'), px('ber-', 'bel-')], 'ajar', [], false)
    };

    // ── prefix candidate generator (the hard part) ───────────────────────
    // Returns ordered {surface, canonical, stem} candidates for ONE prefix layer.
    // Multiple candidates model recoding (meng‑V vs meng‑kV): the enumerator tries
    // them all and the dictionary picks the real root(s).
    // pref (allomorph preference): 3 = a deleted p/t/k/s was put back, 2 = neutral (no
    // morphophonemic change, incl. the legitimate meng-/peng- before a vowel), 1 = the
    // WEAK "no-deletion" fallback (me-/pe- + a retained nasal: me-·nV, me-·mV, me-·nyV).
    // The weak reading exists only for the rare ny-/n-/m-initial roots (nyanyi, nyala);
    // on a word that is itself a dictionary entry it is never the right answer
    // (memori ↛ me-·mori), which the lexicalized gate relies on.
    //
    // sRestore marks meny-/peny- → s. Unlike k/t/p before a vowel, an s-restoration that
    // lands on a dictionary root is a reliable signal (meny- can only come from an
    // s-root), so it is endorsed rather than penalised: menyapu→sapu, not me-·nyapu.
    function prefixCandidates(w) {
        var out = [];
        var add = function (surface, canonical, stem, pref, sRestore) {
            if (stem && stem.length >= 2 && /^[a-z]+$/.test(stem)) {
                out.push({ surface: surface, canonical: canonical, stem: stem, pref: pref || 2,
                           restored: pref === 3, sRestore: !!sRestore });
            }
        };
        var r2 = w.slice(2), r3 = w.slice(3), r4 = w.slice(4), r5 = w.slice(5);
        var c3 = w.charAt(3), c4 = w.charAt(4);

        // plain prefixes (never mutate the root)
        if (w.indexOf('di') === 0) add('di-', 'di-', r2, 2);
        if (w.indexOf('ke') === 0) add('ke-', 'ke-', r2, 2);
        if (w.indexOf('se') === 0) add('se-', 'se-', r2, 2);

        // ber- / be-
        if (w.indexOf('belajar') === 0) add('bel-', 'ber-', 'ajar' + w.slice(7), 2);
        if (w.indexOf('ber') === 0) { add('ber-', 'ber-', r3, 2); if (isV(c3)) add('be-', 'ber-', 'r' + r3, 2); }
        // rule 5: beC1erC2 → be-C1erC2, C1 ∉ {r,l}. The 'er' context is what licenses the
        // bare be- allomorph (bekerja→kerja); without it, be- is not a prefix at all and
        // stripping it shreds ordinary lexemes (beban→be+ban, bebas→be+bas).
        if (w.slice(3, 5) === 'er' && w.length >= 6 && w.indexOf('be') === 0 &&
            w.charAt(2) !== 'r' && w.charAt(2) !== 'l') add('be-', 'ber-', r2, 2);

        // ter- / te-
        if (w.indexOf('ter') === 0) { add('ter-', 'ter-', r3, 2); if (isV(c3)) add('te-', 'ter-', 'r' + r3, 2); }
        // rule 9: teC1erC2 → te-C1erC2, C1 ≠ r (same 'er' licensing as rule 5 — te- is not a
        // free-standing prefix: teman/tetap/tebal are roots, not te+man/te+tap/te+bal)
        if (w.slice(3, 5) === 'er' && w.length >= 6 && w.indexOf('te') === 0 &&
            w.charAt(2) !== 'r') add('te-', 'ter-', r2, 2);

        // per- / pe-  (peN- nasal forms handled in the peN block)
        if (w.indexOf('pelajar') === 0) add('pe-', 'per-', 'ajar' + w.slice(7), 2); // pelajar, pelajaran
        if (w.indexOf('per') === 0) { add('per-', 'per-', r3, 2); if (isV(c3)) add('pe-', 'per-', 'r' + r3, 2); }

        // meN- family
        // menge- attaches ONLY to a monosyllabic root (mengecat, mengebom). Without the
        // syllable check it also eats polysyllabic loanwords: mengevaluasi→menge+valuasi.
        if (w.indexOf('menge') === 0 && syllables(r5) <= 1) add('menge-', 'meN-', r5, 2);
        if (w.indexOf('meng') === 0) {
            if (isV(c4)) { add('meng-', 'meN-', 'k' + r4, 3); add('meng-', 'meN-', r4, 2); }  // k‑deletion | meng‑V (both legitimate)
            else add('meng-', 'meN-', r4, 2);                                                // g,h,q,k(retained)
        }
        if (w.indexOf('meny') === 0) { add('meny-', 'meN-', 's' + r4, 3, true); add('me-', 'meN-', 'ny' + r4, 1); } // sapu | nyanyi
        if (w.indexOf('men') === 0) {
            if (isV(c3)) { add('men-', 'meN-', 't' + r3, 3); add('me-', 'meN-', 'n' + r3, 1); } // t‑deletion | me‑nV
            else add('men-', 'meN-', r3, 2);                                                 // c,d,j,z
        }
        if (w.indexOf('mem') === 0) {
            if (isV(c3)) { add('mem-', 'meN-', 'p' + r3, 3); add('me-', 'meN-', 'm' + r3, 1); } // p‑deletion | me‑mV
            else add('mem-', 'meN-', r3, 2);                                                 // b,f,v and p‑retained
        }
        // me- attaches unchanged ONLY before the liquids/glides. Before m/n/ny the surface
        // is identical but the reading is the weak fallback above (me-·mV), so admitting a
        // neutral me-+anything here would let a lexicalized loanword decompose: memori→me·mori.
        if (w.indexOf('me') === 0 && 'lrwy'.indexOf(w.charAt(2)) !== -1) add('me-', 'meN-', r2, 2);

        // peN- family (mirror of meN-)
        if (w.indexOf('penge') === 0 && syllables(r5) <= 1) add('penge-', 'peN-', r5, 2);
        if (w.indexOf('peng') === 0) {
            if (isV(c4)) { add('peng-', 'peN-', 'k' + r4, 3); add('peng-', 'peN-', r4, 2); }
            else add('peng-', 'peN-', r4, 2);
        }
        if (w.indexOf('peny') === 0) { add('peny-', 'peN-', 's' + r4, 3, true); add('pe-', 'peN-', 'ny' + r4, 1); }
        if (w.indexOf('pen') === 0) {
            if (isV(c3)) { add('pen-', 'peN-', 't' + r3, 3); add('pe-', 'peN-', 'n' + r3, 1); }
            else add('pen-', 'peN-', r3, 2);
        }
        if (w.indexOf('pem') === 0) {
            if (isV(c3)) { add('pem-', 'peN-', 'p' + r3, 3); add('pe-', 'peN-', 'm' + r3, 1); }
            else add('pem-', 'peN-', r3, 2);
        }
        if (w.indexOf('pe') === 0) add('pe-', 'peN-', r2, 2);                                // pelari→lari

        // de-dupe (surface+stem), keeping the highest pref
        var byKey = {}, dedup = [];
        for (var i = 0; i < out.length; i++) {
            var k = out[i].surface + '|' + out[i].stem;
            if (!byKey[k]) { byKey[k] = out[i]; dedup.push(out[i]); }
            else if (out[i].pref > byKey[k].pref) {
                byKey[k].pref = out[i].pref; byKey[k].restored = out[i].restored; byKey[k].sRestore = out[i].sRestore;
            }
        }
        return dedup;
    }

    // suffix candidates whose stage > minStage (strict inward order → one per class)
    function suffixCandidates(w, minStage) {
        var out = [];
        var tryClass = function (list, cls) {
            var stage = SUF_STAGE[cls];
            if (stage < minStage) return;
            for (var i = 0; i < list.length; i++) {
                var s = list[i];
                if (w.length - s.length >= 2 && w.slice(-s.length) === s) {
                    out.push({ surface: s, cls: cls, stage: stage, stem: w.slice(0, w.length - s.length) });
                }
            }
        };
        tryClass(PARTICLE, 'particle');
        tryClass(POSSESSIVE, 'possessive');
        tryClass(DERIV, 'derivational');
        return out;
    }

    function forbidden(prefixCanonical, removedDeriv, currentStem) {
        var bad = FORBIDDEN[prefixCanonical];
        if (!bad) return false;
        for (var i = 0; i < removedDeriv.length; i++) {
            if (bad.indexOf(removedDeriv[i]) !== -1) {
                if (prefixCanonical === 'ke-' && removedDeriv[i] === 'i' && currentStem === 'tahu') continue; // ketahui
                return true;
            }
        }
        return false;
    }

    // Enumerate EVERY valid analysis (each ending in a dict root, plus the trivial
    // whole-word if it is itself a root). Bounded: ≤3 prefixes, ≤6 depth, strict
    // suffix stages. dict.has()-first at each node records that root but keeps
    // exploring, so both "berlari" (whole) and "ber+lari" (deeper) are captured.
    function enumerate(dict, word) {
        var solutions = [], seen = {};
        function record(w, prefixes, suffixes, infix, curatedInfix) {
            var key = w + '|' + prefixes.map(function (p) { return p.canonical + ':' + p.surface; }).join(',') +
                      '|' + suffixes.map(function (s) { return s.type + ':' + s.surface; }).join(',') +
                      '|' + (infix ? infix.surface : '');
            if (seen[key]) return; seen[key] = 1;
            var infl = suffixes.filter(function (s) { return s.type === 'particle' || s.type === 'possessive'; }).length;
            var hasDeriv = suffixes.some(function (s) { return s.type === 'derivational'; });
            var cliticOnly = prefixes.length === 0 && !infix && suffixes.length > 0 &&
                             suffixes.every(function (s) { return s.type === 'particle' || s.type === 'possessive'; });
            solutions.push({
                root: w, prefixes: prefixes.slice(), suffixes: suffixes.slice(), infix: infix || null,
                affixCount: prefixes.length + suffixes.length + (infix ? 1 : 0),
                prefixCount: prefixes.length,
                derivCount: suffixes.filter(function (s) { return s.type === 'derivational'; }).length,
                inflCount: infl,
                prefixPref: prefixes.reduce(function (a, p) { return a + (p.pref || 2); }, 0),
                // −1 endorsed restoration (s-restore, or RESTORE_OK) · 0 nothing restored
                // · +1 speculative k/t/p restoration whose un-restored twin is also a root
                restoreScore: (function () {
                    var hard = prefixes.some(function (p) { return p.restored && !p.sRestore; });
                    if (hard) return RESTORE_OK[w] ? -1 : 1;
                    return prefixes.some(function (p) { return p.sRestore; }) ? -1 : 0;
                }()),
                restoredHard: prefixes.some(function (p) { return p.restored && !p.sRestore; }),
                // weak allomorph that ALSO had to shed a suffix to reach a root
                weakSuffixed: prefixes.some(function (p) { return p.pref === 1; }) && suffixes.length > 0,
                complexPrefix: prefixes.some(function (p) { return PLAIN_PREFIX[p.canonical] !== 1; }),
                cliticOnly: cliticOnly,
                curatedInfix: !!curatedInfix,
                // pref 1 == the "nothing was deleted" fallback allomorph (me-+nV, me-+mV).
                // It is the weakest reading and must not carry a lexicalized word on its own.
                weakAllomorph: prefixes.some(function (p) { return p.pref === 1; }),
                confix: prefixes.length > 0 && hasDeriv
            });
        }
        function dfs(w, prefixes, suffixes, minStage, depth) {
            if (dict.has(w)) record(w, prefixes, suffixes, null);
            if (depth >= 6) return;

            // suffix removal (steps 1–3), strict stage order
            var sc = suffixCandidates(w, minStage);
            for (var i = 0; i < sc.length; i++) {
                var s = sc[i];
                dfs(s.stem, prefixes, suffixes.concat([{ surface: s.surface, type: s.cls }]), s.stage + 1, depth + 1);
            }

            // derivational prefix removal (step 4) with morphophonemic recoding
            if (prefixes.length < 3) {
                var removedDeriv = suffixes.filter(function (x) { return x.type === 'derivational'; })
                                           .map(function (x) { return x.surface; });
                var pc = prefixCandidates(w);
                for (var j = 0; j < pc.length; j++) {
                    var p = pc[j];
                    if (prefixes.length && prefixes[prefixes.length - 1].canonical === p.canonical) continue; // no me‑me‑
                    if (forbidden(p.canonical, removedDeriv, p.stem)) continue;
                    dfs(p.stem, prefixes.concat([{ surface: p.surface, canonical: p.canonical, type: 'prefix',
                                                   pref: p.pref, restored: p.restored, sRestore: p.sRestore }]),
                        suffixes, minStage, depth + 1);
                }
            }

            // infix (sisipan)
            if (w.length >= 5) {
                if (INFIX_CURATED[w] && dict.has(INFIX_CURATED[w][0])) {
                    record(INFIX_CURATED[w][0], prefixes, suffixes, { surface: INFIX_CURATED[w][1] }, true);
                }
                for (var k = 0; k < INFIXES.length; k++) {
                    var inf = INFIXES[k];
                    if (w.slice(1, 1 + inf.length) === inf) {
                        var cand = w.charAt(0) + w.slice(1 + inf.length);
                        if (dict.has(cand)) record(cand, prefixes, suffixes, { surface: '-' + inf + '-' });
                    }
                }
            }
        }
        dfs(word, [], [], 0, 0);
        return solutions;
    }

    // pick the best analysis, in priority order:
    //  1. A curated infix, then (grudgingly) an algorithmic one.
    //  2. Don't invent a deleted consonant when the un-restored root is also a
    //     dictionary word (mengubah→ubah, mengisi→isi, mengaku→aku), unless it is an
    //     s-restore or RESTORE_OK endorses it (menyapu→sapu, mengarang→karang). This
    //     outranks the ≥4-char preference below, or "kisi" (4) beats the correct "isi" (3).
    //  3. Prefer a ≥4-char root when one exists (dimakan→makan, not di·ma·-kan).
    //  4. MORE derivational suffixes — Indonesian derivation is overwhelmingly
    //     confixal, so pe-·kerja·-an beats the lexicalized pe-·kerjaan, and
    //     mem-·per-·baik·-i beats the understemmed mem-·perbaiki.
    //  5. FEWEST clitics — a clitic reached by extra stripping is nearly always
    //     spurious (mengaku ↛ men-·ga·-ku).
    //  6. FEWER prefixes — a single prefix beats a double-prefix overstem
    //     (mengecat: menge-·cat over meng-+ke-·cat).
    //  7. Shortest root (memberdaya-kan → daya, not dayak; pelajar → ajar, not lajar),
    //     then fewest total affixes.
    function best(list) {
        return list.slice().sort(function (a, b) {
            var ac = a.curatedInfix ? 0 : 1, bc = b.curatedInfix ? 0 : 1;
            if (ac !== bc) return ac - bc;                                        // hand-curated infix wins outright (temali→tali·-em-, not te-·mali)
            var ai = a.infix ? 1 : 0, bi = b.infix ? 1 : 0;
            if (ai !== bi) return ai - bi;                                        // an ALGORITHMIC infix is a last resort (pemukul≠p+em+ukul)
            if (a.restoreScore !== b.restoreScore) return a.restoreScore - b.restoreScore; // don't invent a deleted consonant
            var al = a.root.length >= 4 ? 0 : 1, bl = b.root.length >= 4 ? 0 : 1;
            if (al !== bl) return al - bl;                                        // prefer a ≥4-char root when one exists (dimakan→makan, not ma)
            if (b.derivCount !== a.derivCount) return b.derivCount - a.derivCount; // more confix suffixes (pekerjaan→kerja)
            if (a.inflCount !== b.inflCount) return a.inflCount - b.inflCount;    // fewest clitics (mengaku ↛ men-·ga·-ku)
            if (a.prefixCount !== b.prefixCount) return a.prefixCount - b.prefixCount; // fewer prefixes (mengecat)
            if (a.root.length !== b.root.length) return a.root.length - b.root.length; // shorter root (daya over dayak)
            return a.affixCount - b.affixCount;
        })[0];
    }

    // Assemble the public result object (ordered segments for colored rendering).
    function finalize(input, rootWord, prefixes, suffixes, infix, meta) {
        meta = meta || {};
        var dispSuf = suffixes.slice().reverse(); // collected outer→in; display root→out
        var segments = [];
        for (var i = 0; i < prefixes.length; i++) {
            var pType = prefixes[i].type || 'prefix';
            // kind drives the tile's CSS class — a negator (ketidakadilan → ke·tidak·adil·an)
            // must not be painted as a plain prefix.
            segments.push({ text: prefixes[i].surface, kind: pType === 'negator' ? 'negator' : 'prefix',
                            canonical: prefixes[i].canonical, type: pType });
        }
        if (infix) segments.push({ text: infix.surface, kind: 'infix', type: 'infix' });
        segments.push({ text: rootWord, kind: 'root', type: 'root' });
        for (var j = 0; j < dispSuf.length; j++) {
            segments.push({ text: '-' + dispSuf[j].surface, kind: 'suffix', type: dispSuf[j].type });
        }
        var affixCount = prefixes.length + dispSuf.length + (infix ? 1 : 0);
        return {
            input: input,
            normalized: String(input || '').toLowerCase().trim(),
            found: meta.found !== false,
            root: rootWord,
            isPhrase: !!meta.isPhrase,
            prefixes: prefixes.map(function (p) { return { surface: p.surface, canonical: p.canonical, type: p.type || 'prefix' }; }),
            infix: infix ? infix.surface : null,
            suffixes: dispSuf.map(function (s) { return { surface: '-' + s.surface, type: s.type }; }),
            segments: segments,
            affixCount: affixCount,
            isRoot: affixCount === 0,
            reduplication: !!meta.reduplication,
            curated: !!meta.curated
        };
    }

    function notFound(input) {
        var w = String(input || '').toLowerCase().trim();
        return {
            input: input, normalized: w, found: false, root: w, isPhrase: false,
            prefixes: [], infix: null, suffixes: [],
            segments: [{ text: w, kind: 'unknown', type: 'unknown' }],
            affixCount: 0, isRoot: false, reduplication: false, curated: false
        };
    }

    // ── core analyze(dict, word) — pure, dictionary passed in ─────────────
    function analyzeWith(dict, input) {
        var w = String(input || '').toLowerCase().trim().replace(/[^a-z\-]/g, '');
        if (!w) return notFound(input);

        // reduplication: kata ulang (buku-buku, berlari-lari, seindah-indahnya)
        if (w.indexOf('-') !== -1) {
            var parts = w.split('-');
            if (parts.length === 2) {
                var a = analyzeWith(dict, parts[0]);
                var b = analyzeWith(dict, parts[1]);
                if ((a.found && b.found && a.root === b.root) || parts[0] === parts[1]) {
                    var base = Object.assign({}, a.found ? a : b);
                    base.input = input; base.reduplication = true;
                    return base;
                }
                // otherwise a single hyphenated lexeme (mondar-mandir) → leave whole
            }
        }

        if (EXC[w]) return EXC[w](input);
        if (MONO[w]) return finalize(input, w, [], [], null, { found: true }); // closed-class function word

        var sols = enumerate(dict, w);
        var wholeInDict = dict.has(w);
        var nonTrivial = sols.filter(function (s) { return s.affixCount > 0; });

        // A weak allomorph (me-·mV / me-·nV) that must ALSO strip a suffix to reach a root
        // is a stretch: memakai ↛ me-·maka·-i, menahan ↛ me-·nah·-an. Whenever a restored
        // reading exists (mem-·pakai, men-·tahan), it is the better story, so drop those.
        // With no restored rival the weak reading stands (menyanyikan → me-·nyanyi·-kan).
        if (nonTrivial.some(function (s) { return s.restoredHard; })) {
            var trimmed = nonTrivial.filter(function (s) { return !s.weakSuffixed || WEAK_OK[s.root]; });
            if (trimmed.length) nonTrivial = trimmed;
        }
        var pick;

        if (wholeInDict) {
            // Lexicalized-form gate: the whole word is itself a dictionary entry, so
            // only PREFER a decomposition that is confidently derived — (a) it uses a
            // complex prefix (meN-/peN-/ber-/ter-/per-), or (b) it is a clean clitic
            // on a bare root (apapun→apa+‑pun), or (c) it is a confix (prefix + a
            // derivational suffix, e.g. ke‑…‑an), or (d) it is a curated infix
            // (telunjuk→tunjuk·-el-, kinerja→kerja·-in-). A bare plain-prefix split
            // (sekolah→se+kolah), a derivational-suffix-only split (makan→mak+‑an) or a
            // spurious clitic reached through more stripping (sekolah→se+ko+‑lah) are
            // rejected → keep the word whole. Clitic-only splits are NOT trusted for
            // dict words (punya→pu+‑nya, sekolah→seko+‑lah are wrong); genuine
            // clitic words go through EXC.
            //
            // The root must also be POLYSYLLABIC (≥2 vowels). Indonesian roots that
            // license a lexicalized derivation are ≥2 syllables (kerja, muda, hasil,
            // atur); a monosyllabic "root" recovered from a dictionary word is the
            // signature of an overstem (menang→men+tang, penting→pen+ting,
            // ternak→ter+nak). Genuine monosyllabic roots (cat, tik, bom) only surface
            // through menge-/penge- forms, which are not dictionary entries and so
            // never reach this gate.
            var accepted = nonTrivial.filter(function (s) {
                if (s.curatedInfix) return true;
                // The word IS a word — don't reach a root by inventing a deleted consonant
                // (menara↛men+tara) or by the weakest allomorph (memori↛me+mori), unless a
                // confix vouches for the derivation (pengalaman→peng+alam+an).
                if (s.restoreScore > 0 && !s.confix) return false;
                if (s.weakAllomorph && !s.confix && !WEAK_OK[s.root]) return false;
                return (s.complexPrefix || s.confix) && s.root.length >= 3 && syllables(s.root) >= 2;
            });
            if (accepted.length) pick = best(accepted);
            else return finalize(input, w, [], [], null, { found: true }); // keep whole root
        } else {
            if (nonTrivial.length) pick = best(nonTrivial);
            else return notFound(input);
        }
        return finalize(input, pick.root, pick.prefixes, pick.suffixes, pick.infix, { found: true });
    }

    // ── browser wrapper: lazy dictionary load + caching ──────────────────
    var _dict = null, _loading = null, _cache = Object.create(null);
    var DEFAULT_URL = '/data/indonesian/kata-dasar.txt';

    function buildSet(text) {
        var set = new Set();
        var lines = text.split('\n');
        for (var i = 0; i < lines.length; i++) {
            var t = lines[i].trim();
            if (t) set.add(t);
        }
        return set;
    }

    var IndoMorph = {
        load: function (url) {
            if (_dict) return Promise.resolve(_dict);
            if (_loading) return _loading;
            _loading = fetch(url || DEFAULT_URL, { cache: 'force-cache' })
                .then(function (r) { if (!r.ok) throw new Error('dict ' + r.status); return r.text(); })
                .then(function (txt) { _dict = buildSet(txt); return _dict; })
                // A failed load must not be sticky: the page offers «retry», and
                // a retry that hands back the same rejected promise is a button
                // that does nothing (Sentry TIMBRICA-FRONTEND-JH).
                .catch(function (e) { _loading = null; throw e; });
            return _loading;
        },
        get ready() { return !!_dict; },
        size: function () { return _dict ? _dict.size : 0; },
        analyze: function (word) {
            if (!_dict) throw new Error('IndoMorph: call load() first');
            var key = String(word || '').toLowerCase().trim();
            if (_cache[key]) return _cache[key];
            return (_cache[key] = analyzeWith(_dict, word));
        },
        analyzeAsync: function (word, url) {
            return this.load(url).then(function () { return IndoMorph.analyze(word); });
        },
        has: function (word) { return _dict ? _dict.has(String(word || '').toLowerCase().trim()) : false; }
    };

    // Node/test surface: expose the pure core + a Set builder.
    IndoMorph.analyzeWith = analyzeWith;
    IndoMorph.buildSet = buildSet;
    IndoMorph.enumerate = enumerate;
    IndoMorph._prefixCandidates = prefixCandidates;

    return IndoMorph;
}));
